<template>
  <div class="bg-white h-auto px-5 rounded-lg">
    <div
      class="flex flex-col lg:flex-row md:items-center lg:gap-2 border-b-[1.5px] border-primary-purple-100 py-5"
    >
      <div class="flex items-center w-full justify-between">
        <Heading :heading="graphHeading" heading-size="text-[18px]" heading-padding="py-0" />
        <div
          v-permission="EMAIL_AND_SMS_FILTERS_PERMISSION"
          class="xl:h-11 md:h-10 h-9 flex items-center cursor-pointer justify-center gap-2 bg-white border border-primary-gray-300 rounded-lg px-2"
        >
          <icon icon="filter" height="20" width="20" color="primary-gray-700" />
          <span class="font-inter font-semibold text-primary-gray-700 text-sm hidden sm:block">
            Filters
          </span>
        </div>
      </div>
      <div
        class="w-full lg:w-fit flex flex-wrap-reverse sm:flex-nowrap gap-2 items-end justify-end"
      >
        <UiFilterBox
          v-model="selectedCampus"
          :options="campusOptions"
          :is-switch-box="true"
          :is-disabled="isCampusDisabled"
          label="title"
          reduce="id"
        />
        <CalendarActionsWrapper
          :is-future-date-disabled="true"
          :date-format="DATETIME_CONSTANT.SHORT_MONTH_FULL_YEAR"
          :calendar-date="selectedDate"
          filter-view="Month"
          custom-class="right-0 before:right-12"
          @selectDate="onDateChange"
          @selectDateRange="updateDateRange"
        />
      </div>
    </div>
    <div class="overflow-auto">
      <GraphView :chart-data="graphData" class="min-w-12" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { getDatesForMonth } from '@src/utils/moment.util.js'
import Heading from '@/src/components/Heading.vue'
import GraphView from '@src/router/views/graph/graphView.vue'
import CalendarActionsWrapper from '@src/components/Calendar/CalendarActionsWrapper.vue'
import UiFilterBox from '@/src/components/UiElements/UiFilterBox.vue'
import icon from '@src/components/icons/icon.vue'
import { arrayRange, generateRandomArray } from '@utils/generalUtil'
import { useStore } from 'vuex'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import DATETIME_CONSTANT from '@src/constants/date-time-constants.js'
import { ENV } from '@src/constants/general-constants.js'

const currentClassScope = computed(() => store.state.layout.currentClassScope)
const currentSectionScope = computed(() => store.state.layout.currentSectionScope)
const selectedClass = ref('')
const classOptions = ref([])
const isCampusDisabled = computed(() => !!currentCampusScope.value)
const selectedCampus = ref('')
const currentCampusScope = computed(() => store.state.layout.currentCampusScope)
const store = useStore()
const graphHeading = 'Emails/SMS Sent'
const selectedDate = ref(new Date())
const campusOptions = computed(() => store.state.layout.campuses)
const EMAIL_AND_SMS_FILTERS_PERMISSION = {
  env: [ENV.DEV],
}
const onDateChange = (date) => {
  selectedDate.value = date
  generateAnnouncementGraphStats()
}

const updateDateRange = (range) => {
  if (!range) return
  if (range.start) {
    selectedDate.value = range.start
  } else {
    selectedDate.value = range
  }
  generateAnnouncementGraphStats()
}

const generateAnnouncementGraphStats = async () => {
  if (currentCampusScope.value) selectedCampus.value = currentCampusScope.value.id
  if (currentClassScope.value) selectedClass.value = currentClassScope.value.id
  classOptions.value = currentClassScope.value
  graphData.value.tooltipHeader = getDatesForMonth(selectedDate.value)
  const newdatasets = [
    {
      label: 'New Users',
      data: generateRandomArray(30, 50, 100),
      borderColor: '#F63D68',
      backgroundColor: '#F63D68',
      tension: 0.5,
      pointRadius: 0,
      pointHoverRadius: 0,
    },
    {
      label: 'Email sent',
      data: generateRandomArray(30, 50, 100),
      borderColor: '#7F56D9',
      backgroundColor: '#7F56D9',
      tension: 0.5,
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: {
        target: 'origin',
        above: '#7F56D9)',
      },
    },
    {
      label: 'SMS',
      data: generateRandomArray(30, 50, 100),
      borderColor: '#F79009',
      backgroundColor: '#F79009',
      tension: 0.5,
      pointRadius: 0,
      pointHoverRadius: 0,
    },
    {
      label: 'Announcements',
      data: generateRandomArray(30, 50, 100),
      borderColor: '#1570EF',
      backgroundColor: '#1570EF',
      tension: 0.5,
      pointRadius: 0,
      pointHoverRadius: 0,
    },
  ]

  graphData.value.data.datasets = newdatasets
}
onMounted(() => {
  generateAnnouncementGraphStats()
})
watch([currentCampusScope, currentClassScope, currentSectionScope], () => {
  generateAnnouncementGraphStats()
})
watch([selectedCampus], () => {
  fetchClassList().then(() => {
    generateAnnouncementGraphStats()
  })
})

const fetchClassList = async () => {
  let query = {
    $where: buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', selectedCampus.value),
  }

  const response = await getClasses(query)
  if (response) classOptions.value = response
  else classOptions.value = []
}

const graphData = ref({
  type: 'line',
  tooltipHeader: [],
  data: {
    labels: arrayRange(1, 30),
    datasets: [],
  },
  options: {
    plugins: {
      tooltip: { backgroundColor: 'white', titleColor: 'black', bodyColor: '#667085' },
      legend: {
        display: true,
        align: 'end',
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
          padding: 13,
          font: {
            size: 14,
          },
          textAlign: 'center',
        },
      },
    },

    animations: {
      radius: {
        duration: 400,
        easing: 'linear',
        loop: (context) => context.active,
      },
    },
    hoverRadius: 12,
    hoverBackgroundColor: 'yellow',
    interaction: {
      mode: 'nearest',
      intersect: false,
      axis: 'x',
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
    },
    aspectRatio: 100 / 14.3,
  },
})
const getClasses = (payload) => store.dispatch('classes/getUserClasses', payload)
</script>
